// Workflow data for handling user data analysis queries
// Supports multiple query types with different simulations

// Original workflow: "Show me all attributes which have low completeness score and exclude those and build a view of customer 360"
const createCompletenessWorkflow = (userQuestion) => ({
  sessionId: `session-${Date.now()}`,
  timestamp: new Date().toISOString(),
  
  // User's question
  userQuestion: userQuestion,
  
  // DeepAgent's initial plan (TODO list)
  plan: {
    thinking: "I need to identify columns with low completeness scores across customer-related tables, exclude them, and then build a comprehensive customer 360 view from the remaining high-quality attributes.",
    todos: [
      {
        id: 1,
        text: "Identify all attributes (columns) with low completeness score using metadata_explorer.",
        status: "completed"
      },
      {
        id: 2, 
        text: "Exclude columns with low completeness from the customer 360 view.",
        status: "completed"
      },
      {
        id: 3,
        text: "Build a customer 360 view using the remaining attributes by creating a data product (view).",
        status: "completed"
      }
    ]
  },
  
  // Sub-agents that respond individually
  subAgents: [
    {
      id: "metadata-agent",
      name: "MetaDataExploration Agent",
      description: "Exploring database schema and metadata for completeness analysis",
      handoffMessage: "User wants to analyze completeness scores across attributes. This requires exploring table metadata and schema information — delegating to metadata_explorer.",
      status: "completed",
      duration: "41.04s",
      steps: [
        {
          id: "meta-1",
          name: "Analyzing Question",
          status: "completed",
          duration: "0.53s",
          output: "Identified need to explore table schemas for data completeness analysis across customer-related tables"
        },
        {
          id: "meta-2",
          name: "Retrieving Table Metadata",
          status: "completed",
          duration: "12.31s",
          output: "Exploring database schema to identify customer-related tables (kna1, vbak)"
        },
        {
          id: "meta-3",
          name: "Generating Analysis Code",
          status: "completed",
          duration: "5.83s",
          hasQuery: true,
          query: `# Analyzing completeness scores for customer-related columns
import pandas as pd

# Query to retrieve column completeness from metadata
completeness_query = """
SELECT 
  table_name,
  column_name,
  COUNT(*) as total_rows,
  COUNT(column_value) as non_null_count,
  ROUND(COUNT(column_value)::float / COUNT(*) * 100, 2) as completeness_pct
FROM information_schema.columns c
LEFT JOIN data_profiling_results d ON c.column_name = d.column_name
WHERE c.table_name IN ('kna1', 'vbak')
GROUP BY table_name, column_name
ORDER BY completeness_pct ASC;
"""

# Execute and analyze results
df = execute_query(completeness_query)
low_completeness = df[df['completeness_pct'] < 95]
print(f"Columns with low completeness: {len(low_completeness)}")`,
          data: [
            { table_name: "kna1", column_name: "name1", completeness_pct: 100.00 },
            { table_name: "kna1", column_name: "land1", completeness_pct: 100.00 },
            { table_name: "kna1", column_name: "kunnr", completeness_pct: 100.00 },
            { table_name: "vbak", column_name: "vbeln", completeness_pct: 100.00 },
            { table_name: "vbak", column_name: "auart", completeness_pct: 100.00 }
          ]
        },
        {
          id: "meta-4",
          name: "Schema Analysis Complete",
          status: "completed",
          duration: "22.37s",
          output: "Identified customer-related tables (kna1, vbak) with 13 total columns. All columns have 100% completeness - no columns need to be excluded."
        }
      ],
      response: "Successfully analyzed metadata. Found customer-related tables (kna1, vbak) with 13 columns. All columns have 100% completeness scores - no columns with low completeness were found."
    },
    {
      id: "query-agent",
      name: "DataTransformation Agent", 
      description: "Executing SQL queries and creating customer 360 view",
      handoffMessage: "Metadata exploration complete. Now need to run completeness calculations and build the customer 360 view — handing off to data_transformation.",
      status: "completed",
      duration: "174.79s",
      steps: [
        {
          id: "query-1",
          name: "Analyzing Question",
          status: "completed",
          duration: "0.42s",
          output: "Need to calculate completeness scores for all columns and create a customer 360 view excluding low-completeness columns"
        },
        {
          id: "query-2",
          name: "Generating SQL for Completeness Analysis",
          status: "completed",
          duration: "10.84s",
          output: "Created SQL queries to check column completeness scores across kna1 and vbak tables"
        },
        {
          id: "query-3",
          name: "Executing Completeness Check Query",
          status: "completed",
          duration: "38.25s",
          hasQuery: true,
          query: `WITH kna1_completeness AS (
  SELECT
    'name1' AS column_name, COUNT(name1)::float / NULLIF(COUNT(*),0) AS completeness FROM kna1 UNION ALL
    SELECT 'land1', COUNT(land1)::float / NULLIF(COUNT(*),0) FROM kna1 UNION ALL
    SELECT 'regio', COUNT(regio)::float / NULLIF(COUNT(*),0) FROM kna1 UNION ALL
    SELECT 'ort01', COUNT(ort01)::float / NULLIF(COUNT(*),0) FROM kna1 UNION ALL
    SELECT 'pstlz', COUNT(pstlz)::float / NULLIF(COUNT(*),0) FROM kna1 UNION ALL
    SELECT 'kunnr', COUNT(kunnr)::float / NULLIF(COUNT(*),0) FROM kna1
),
vbak_completeness AS (
  SELECT 'auart', COUNT(auart)::float / NULLIF(COUNT(*),0) FROM vbak UNION ALL
    SELECT 'vkorg', COUNT(vkorg)::float / NULLIF(COUNT(*),0) FROM vbak UNION ALL
    SELECT 'vtweg', COUNT(vtweg)::float / NULLIF(COUNT(*),0) FROM vbak UNION ALL
    SELECT 'spart', COUNT(spart)::float / NULLIF(COUNT(*),0) FROM vbak UNION ALL
    SELECT 'audat', COUNT(audat)::float / NULLIF(COUNT(*),0) FROM vbak UNION ALL
    SELECT 'bstnk', COUNT(bstnk)::float / NULLIF(COUNT(*),0) FROM vbak UNION ALL
    SELECT 'kunnr', COUNT(kunnr)::float / NULLIF(COUNT(*),0) FROM vbak UNION ALL
    SELECT 'vbeln', COUNT(vbeln)::float / NULLIF(COUNT(*),0) FROM vbak
)
SELECT * FROM kna1_completeness
UNION ALL
SELECT * FROM vbak_completeness
ORDER BY column_name;`,
          data: [
            { column_name: "auart", table: "vbak", completeness: "100%" },
            { column_name: "audat", table: "vbak", completeness: "100%" },
            { column_name: "bstnk", table: "vbak", completeness: "100%" },
            { column_name: "kunnr", table: "kna1", completeness: "100%" },
            { column_name: "kunnr", table: "vbak", completeness: "100%" },
            { column_name: "land1", table: "kna1", completeness: "100%" },
            { column_name: "name1", table: "kna1", completeness: "100%" },
            { column_name: "ort01", table: "kna1", completeness: "100%" },
            { column_name: "pstlz", table: "kna1", completeness: "100%" },
            { column_name: "regio", table: "kna1", completeness: "100%" },
            { column_name: "spart", table: "vbak", completeness: "100%" },
            { column_name: "vbeln", table: "vbak", completeness: "100%" },
            { column_name: "vkorg", table: "vbak", completeness: "100%" },
            { column_name: "vtweg", table: "vbak", completeness: "100%" }
          ]
        },
        {
          id: "query-4",
          name: "Building Customer 360 View",
          status: "completed",
          duration: "13.10s",
          hasQuery: true,
          query: `SELECT
  kna1.kunnr,
  kna1.name1,
  kna1.land1,
  kna1.regio,
  kna1.ort01,
  kna1.pstlz,
  vbak.vbeln,
  vbak.auart,
  vbak.vkorg,
  vbak.vtweg,
  vbak.spart,
  vbak.audat,
  vbak.bstnk
FROM kna1
JOIN vbak ON kna1.kunnr = vbak.kunnr;`,
          data: [
            { kunnr: "CUST001", name1: "Acme Corporation", land1: "US", regio: "CA", vbeln: "0000010001", auart: "TA", audat: "2026-01-15" },
            { kunnr: "CUST002", name1: "Tech Industries", land1: "DE", regio: "BY", vbeln: "0000010002", auart: "SO", audat: "2026-01-18" },
            { kunnr: "CUST003", name1: "Global Solutions Ltd", land1: "UK", regio: "LON", vbeln: "0000010003", auart: "TA", audat: "2026-01-20" },
            { kunnr: "CUST004", name1: "Pacific Trading Co", land1: "JP", regio: "TK", vbeln: "0000010004", auart: "ZOR", audat: "2026-01-22" },
            { kunnr: "CUST005", name1: "Nordic Systems AB", land1: "SE", regio: "ST", vbeln: "0000010005", auart: "SO", audat: "2026-01-23" }
          ]
        }
      ],
      response: {
        introText: "The unified Customer 360 view brings together key customer records from both SAP and JDE systems, enabling a comprehensive perspective on order and billing activity. Below is a sample of top customers from each source, highlighting representative fields and values for cross-system comparison.",
        table: {
          title: "Unified Customer 360 View – Sample Records from SAP & JDE",
          columns: ["Source System", "Customer ID", "Customer Name", "City", "Country"],
          rows: [
            { "Source System": "JDE", "Customer ID": "207677", "Customer Name": "COLAS RAIL EGYPT - L3 PHASE 3", "City": "-", "Country": "EG" },
            { "Source System": "JDE", "Customer ID": "177217", "Customer Name": "EXYTE FRANCE", "City": "-", "Country": "FR" },
            { "Source System": "JDE", "Customer ID": "198510", "Customer Name": "EIFFAGE EN SYST CLEVIA CENTRE EST", "City": "-", "Country": "FR" },
            { "Source System": "SAP", "Customer ID": "0000021088", "Customer Name": "CE FLORIDA", "City": "ORLANDO", "Country": "US" },
            { "Source System": "SAP", "Customer ID": "0000020080", "Customer Name": "MINGLEDORFFS INC", "City": "NORCROSS", "Country": "US" },
            { "Source System": "SAP", "Customer ID": "0000080240", "Customer Name": "SIGLER - CALIFORNIA", "City": "BREA", "Country": "US" },
            { "Source System": "SAP", "Customer ID": "0000041246", "Customer Name": "CARRIER ENTERPRISE SOUTH CENTRAL", "City": "CARROLLTON", "Country": "US" },
            { "Source System": "SAP", "Customer ID": "0000071116", "Customer Name": "CARRIER ENTERPRISE MEXICO", "City": "BENITO JUAREZ MEXICO", "Country": "MX" }
          ]
        },
        directAnswer: "This table presents sample records from the unified Customer 360 view, integrating both SAP and JDE sources. It showcases customer identifiers, order and billing metrics, and representative values from each system, with SAP records providing detailed location and billing data, while JDE records focus on order activity.",
        insight: "SAP customers display richer location and billing details, including city, country, region, and multi-currency billing, whereas JDE records emphasize order value and currency but lack granular location and billing data. This highlights the importance of harmonizing data fields for true 360-degree visibility."
      }
    },
    {
      id: "response-agent",
      name: "ResponseGeneration Agent",
      description: "Generating final summary and visual outputs",
      handoffMessage: "Data transformation complete. All queries executed successfully — preparing final response with insights and recommendations.",
      status: "completed", 
      duration: "11.08s",
      steps: [
        {
          id: "response-1",
          name: "Analyzing Results",
          status: "completed",
          duration: "2.14s",
          output: "Processing query results to generate comprehensive insights"
        },
        {
          id: "response-2",
          name: "Generating Summary",
          status: "completed",
          duration: "1.50s",
          output: "Creating markdown summary with key findings about data completeness"
        },
        {
          id: "response-3",
          name: "Creating Visual Card",
          status: "completed",
          duration: "0.50s",
          output: "Columns with 100% Completeness: All 13 customer-related columns are fully complete and included in the 360 view."
        },
        {
          id: "response-4",
          name: "Formatting Final Output",
          status: "completed",
          duration: "6.94s",
          output: "Compiling all results with tables and visualizations into final response"
        }
      ],
      response: "**Direct Answer:** All columns from the customer-related tables (kna1, vbak) have 100% completeness and are included in the customer 360 view. This ensures that the resulting dataset is comprehensive and free of missing values."
    }
  ],
  
  // DeepAgent's final summary
  summary: {
    title: "Customer 360 Analysis Complete",
    content: `Based on my analysis of your database, I found that **all 13 columns** in customer-related tables have **100% completeness**.

**Key Findings:**
- Tables analyzed: \`kna1\` (customer master), \`vbak\` (sales documents)
- All columns have complete data (no null values)
- No columns were excluded from the Customer 360 view

**Customer 360 View Created:**
The view joins customer master data with sales document information, providing a comprehensive multi-transactional perspective for each customer, uniquely identified by the combination of \`kunnr\` (customer number) and \`vbeln\` (sales document number).

**Columns Included:**
- **Customer attributes (kna1):** kunnr, name1, land1, regio, ort01, pstlz
- **Sales document attributes (vbak):** vbeln, auart, vkorg, vtweg, spart, audat, bstnk

**Recommendation:**
Your data quality is excellent! Consider setting up monitoring to maintain these high completeness scores. You may also want to apply a custom completeness threshold (e.g., 95%) if you want to flag attributes that are close to having missing data.`,
    actions: [
      { label: "View Customer 360", icon: "database" },
      { label: "Export Results", icon: "download" },
      { label: "Set Up Monitoring", icon: "bell" }
    ]
  },
  
  // Metadata from the actual trace
  metadata: {
    totalDuration: "233.37s",
    traceId: `trace-${Date.now()}`,
    tablesScanned: 2,
    columnsAnalyzed: 13,
    issuesFound: 0
  }
});

// New workflow: "Show me top 5 customers"
const createTopCustomersWorkflow = (userQuestion) => ({
  sessionId: `session-${Date.now()}`,
  timestamp: new Date().toISOString(),
  
  // User's question
  userQuestion: userQuestion,
  
  // DeepAgent's initial plan (TODO list)
  plan: {
    thinking: "I need to identify the top 5 customers based on relevant metrics. I'll first understand what 'top' means in this context - likely by claim count, revenue, or order frequency - then query the appropriate tables.",
    todos: [
      {
        id: 1,
        text: "Understand the request and identify relevant ranking criteria for 'top customers'.",
        status: "completed"
      },
      {
        id: 2, 
        text: "Locate tables containing customer and transaction data.",
        status: "completed"
      },
      {
        id: 3,
        text: "Generate and execute query to retrieve top 5 customers by the identified metric.",
        status: "completed"
      }
    ]
  },
  
  // Sub-agents that respond individually
  subAgents: [
    {
      id: "data-exploration-agent",
      name: "DataExploration",
      description: "retrieving the top 5 customers based on relevant metrics such as revenue, purchase frequency, or total spend",
      handoffMessage: "User wants to find top 5 customers. This requires exploring customer and transaction data to identify ranking criteria — delegating to data_exploration.",
      status: "completed",
      duration: "144.33s",
      steps: [
        {
          id: "explore-1",
          name: "Understanding the Request",
          status: "completed",
          duration: "3.01s",
          output: "User is asking for top 5 customers. Need to determine ranking criteria - will analyze available metrics like claim count, order value, or transaction frequency."
        },
        {
          id: "explore-2",
          name: "Identifying all available tables to locate customer-related data",
          status: "completed",
          duration: "0.06s",
          output: "Found customer tables: patients, claims, encounters. Best metric for ranking: claim count per customer."
        },
        {
          id: "explore-3",
          name: "Retrieving business logic and examples for defining 'top customers' and ranking criteria",
          status: "completed",
          duration: "0.32s",
          output: "Business logic indicates 'top customers' are typically ranked by activity level. Claim count is the primary metric for customer engagement."
        },
        {
          id: "explore-4",
          name: "Identifying the best tables containing customer and transaction/order data for ranking top customers",
          status: "completed",
          duration: "1.54s",
          output: "Selected top_patients_by_claims table which aggregates claim counts per customer. This provides the most relevant ranking data."
        },
        {
          id: "explore-5",
          name: "Reviewing the structure and key fields of the selected tables to determine how to rank customers",
          status: "completed",
          duration: "0.17s",
          output: "Table structure: first_name, last_name, claim_count. Will order by claim_count DESC and limit to 5 records."
        },
        {
          id: "explore-6",
          name: "Generating a query to retrieve the top 5 customers by claim count from the top_patients_by_claims table",
          status: "completed",
          duration: "1.50s",
          hasQuery: true,
          query: `SELECT 
  first_name,
  last_name,
  claim_count
FROM top_patients_by_claims
ORDER BY claim_count DESC
LIMIT 5;`,
          data: [
            { first_name: "rozella39", last_name: "mertz280", claim_count: 3551 },
            { first_name: "sung603", last_name: "weber641", claim_count: 2822 },
            { first_name: "warren653", last_name: "ullrich385", claim_count: 2244 },
            { first_name: "randal152", last_name: "herman763", claim_count: 2073 },
            { first_name: "darla154", last_name: "hoppe518", claim_count: 1446 }
          ]
        },
        {
          id: "explore-7",
          name: "Retrieving the top 5 customers by claim count for user display",
          status: "completed",
          duration: "19.19s",
          output: "Query executed successfully. Retrieved top 5 customers ranked by claim count. Rozella39 Mertz280 leads with 3,551 claims."
        }
      ],
      response: {
        introText: "Here are your top 5 customers ranked by claim count. This overview highlights your most active accounts, enabling targeted engagement and support strategies.",
        highlight: {
          icon: "users",
          title: "Top Customer",
          subtitle: "Rozella39 Mertz280 leads with 3,551 claims.",
          value: "3,551",
          trend: "up"
        },
        table: {
          title: "Top 5 Customers by Claim Count",
          columns: ["First Name", "Last Name", "Claim Count"],
          rows: [
            { "First Name": "rozella39", "Last Name": "mertz280", "Claim Count": "3551" },
            { "First Name": "sung603", "Last Name": "weber641", "Claim Count": "2822" },
            { "First Name": "warren653", "Last Name": "ullrich385", "Claim Count": "2244" },
            { "First Name": "randal152", "Last Name": "herman763", "Claim Count": "2073" },
            { "First Name": "darla154", "Last Name": "hoppe518", "Claim Count": "1446" }
          ]
        },
        chart: {
          title: "Claim Volume of Top 5 Customers",
          type: "bar",
          data: [
            { name: "rozella39", value: 3551 },
            { name: "sung603", value: 2822 },
            { name: "warren653", value: 2244 },
            { name: "randal152", value: 2073 },
            { name: "darla154", value: 1446 }
          ]
        },
        insight: "Rozella39 Mertz280 is your most active customer, followed by Sung603 Weber641 and Warren653 Ullrich385. The claim volume drops significantly after the top two, indicating a strong concentration of activity among your leading accounts. Would you like to see claim trends over time for these customers or explore their claim types?",
        downloadLink: "Click here to download all records"
      }
    }
  ],
  
  // DeepAgent's final summary
  summary: {
    title: "Top 5 Customers Analysis Complete",
    content: `Successfully retrieved the top 5 customers ranked by claim count.

**Key Findings:**
- **#1 Rozella39 Mertz280** leads with 3,551 claims
- **#2 Sung603 Weber641** follows with 2,822 claims  
- Top 2 customers account for over 50% of claims among top 5

**Insights:**
The claim volume shows a clear concentration at the top, with a significant drop-off after the second customer. This suggests these accounts may benefit from dedicated account management.

**Recommendations:**
- Consider VIP programs for top customers
- Analyze claim patterns to predict future high-value customers
- Monitor for churn risk among top accounts`,
    actions: [
      { label: "View Customer Details", icon: "users" },
      { label: "Export Data", icon: "download" },
      { label: "Analyze Trends", icon: "trending-up" }
    ]
  },
  
  // Metadata from the actual trace
  metadata: {
    totalDuration: "144.33s",
    traceId: `trace-${Date.now()}`,
    tablesScanned: 1,
    rowsReturned: 5,
    issuesFound: 0
  }
});

// Main function that routes to the appropriate workflow based on the query
export const createQueryWorkflow = (userQuestion) => {
  const lowerQuestion = userQuestion.toLowerCase();
  
  // Check for "top 5 customers" or similar patterns
  if (lowerQuestion.includes('top') && lowerQuestion.includes('customer')) {
    return createTopCustomersWorkflow(userQuestion);
  }
  
  // Check for "customer 360" pattern - triggers completeness workflow
  if (lowerQuestion.includes('customer 360') || lowerQuestion.includes('customer360')) {
    return createCompletenessWorkflow(userQuestion);
  }
  
  // Default to the completeness workflow for all other queries
  return createCompletenessWorkflow(userQuestion);
};

export default createQueryWorkflow;
